import { Action, createReducer, on } from '@ngrx/store';

import * as BookmarkActions from '../actions/bookmark.actions';
import { Bookmark } from '../models/bookmark';
import { AppError } from '../errors/app-error';
import { AddBookmarkResponse } from '../models/add-bookmark-response';
import { DeleteBookmarkResponse } from '../models/delete-bookmark-response';
import { EnglishProblem, HistoryProblem, NationalLanguageProblem, ScienceProblem } from '../models/problem';
import { SubjectId } from '../resources/config';

export const bookmarkFeatureKey = 'bookmark';

export interface AddBookmarkResult {
  success: boolean;
  response: AddBookmarkResponse | null;
  error: AppError | null;
}

export interface DeleteBookmarkResult {
  success: boolean;
  response: DeleteBookmarkResponse | null;
  error: AppError | null;
}

export interface State {
  bookmark: Bookmark | null;
  addBookmarkResult: AddBookmarkResult | null;
  deleteBookmarkResult: DeleteBookmarkResult | null;

  bookmarkCount: number | null;
  bookmarkSearching: boolean;

  englishBookmarkProblems: EnglishProblem[] | null;
  mathBookmarkProblems: ScienceProblem[] | null;
  nationalLanguageBookmarkProblems: NationalLanguageProblem[] | null;
  physicsBookmarkProblems: ScienceProblem[] | null;
  chemistryBookmarkProblems: ScienceProblem[] | null;
  biologyBookmarkProblems: ScienceProblem[] | null;
  japaneseHistoryBookmarkProblems: HistoryProblem[] | null;
  worldHistoryBookmarkProblems: HistoryProblem[] | null;
  geographyBookmarkProblems: ScienceProblem[] | null;
  politicalEconomyBookmarkProblems: ScienceProblem[] | null;
  specialAdmissionsBookmarkProblems: ScienceProblem[] | null;

  matchedBookmarkProblemIds: string[] | null;
  isBookmarkProblemIdsSearching: boolean;
}

export const initialState: State = {
  bookmark: null,
  addBookmarkResult: null,
  deleteBookmarkResult: null,
  bookmarkCount: 0,
  bookmarkSearching: false,
  englishBookmarkProblems: null,
  mathBookmarkProblems: null,
  nationalLanguageBookmarkProblems: null,
  physicsBookmarkProblems: null,
  chemistryBookmarkProblems: null,
  biologyBookmarkProblems: null,
  japaneseHistoryBookmarkProblems: null,
  worldHistoryBookmarkProblems: null,
  geographyBookmarkProblems: null,
  politicalEconomyBookmarkProblems: null,
  specialAdmissionsBookmarkProblems: null,
  matchedBookmarkProblemIds: null,
  isBookmarkProblemIdsSearching: false
};

const BookmarkReducer = createReducer(
  initialState,

  // Find Bookmarks -------------------------------------------------------
  on(BookmarkActions.findBookmarkSuccess, (state, { bookmark }) => ({ ...state, bookmark })),
  on(BookmarkActions.initializeFindBookmarkState, state => {
    const next: State = { ...state, bookmark: null };
    return next;
  }),

  // Add Bookmark ----------------------------------------------------------------
  on(BookmarkActions.addBookmarkSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      addBookmarkResult: { success: true, response, error: null }
    };
    return next;
  }),
  on(BookmarkActions.addBookmarkFailure, (state, { error }) => {
    const next: State = {
      ...state,
      addBookmarkResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkActions.initializeAddBookmarkState, state => {
    const next: State = { ...state, addBookmarkResult: null };
    return next;
  }),

  // Delete Bookmark ----------------------------------------------------------------
  on(BookmarkActions.deleteBookmarkSuccess, (state, { response }) => {
    const next: State = {
      ...state,
      deleteBookmarkResult: { success: true, response, error: null }
    };
    return next;
  }),
  on(BookmarkActions.deleteBookmarkFailure, (state, { error }) => {
    const next: State = {
      ...state,
      deleteBookmarkResult: { success: false, response: null, error }
    };
    return next;
  }),
  on(BookmarkActions.initializeDeleteBookmarkState, state => {
    const next: State = { ...state, deleteBookmarkResult: null };
    return next;
  }),

  // Search Bookmark Count -------------------------------------------------------
  on(BookmarkActions.findBookmarkCount, state => ({ ...state })),
  on(BookmarkActions.findBookmarkCountSuccess, (state, { response }) => ({
    ...state,
    bookmarkCount: response.count
  })),
  on(BookmarkActions.findBookmarkCountFailure, state => ({ ...state, bookmarkCount: 0 })),
  on(BookmarkActions.initializeFindBookmarkCountState, state => {
    const next: State = { ...state, bookmarkCount: null };
    return next;
  }),

  // Search Bookmark Problems -------------------------------------------------------
  on(BookmarkActions.findBookmarkProblems, state => ({ ...state, bookmarkSearching: true })),
  on(BookmarkActions.findBookmarkProblemsSuccess, (state, { subjectId, problems }) => {
    switch (subjectId) {
      case SubjectId.ENGLISH:
        return { ...state, englishBookmarkProblems: problems as EnglishProblem[], bookmarkSearching: false };
      case SubjectId.MATH:
        return { ...state, mathBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.NATIONAL_LANGUAGE:
        return { ...state, nationalLanguageBookmarkProblems: problems as NationalLanguageProblem[], bookmarkSearching: false };
      case SubjectId.PHYSICS:
        return { ...state, physicsBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.CHEMISTRY:
        return { ...state, chemistryBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.BIOLOGY:
        return { ...state, biologyBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.JAPANESE_HISTORY:
        return { ...state, japaneseHistoryBookmarkProblems: problems as HistoryProblem[], bookmarkSearching: false };
      case SubjectId.WORLD_HISTORY:
        return { ...state, worldHistoryBookmarkProblems: problems as HistoryProblem[], bookmarkSearching: false };
      case SubjectId.GEOGRAPHY:
        return { ...state, geographyBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.POLITICAL_ECONOMY:
        return { ...state, politicalEconomyBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      case SubjectId.SPECIAL_ADMISSIONS:
        return { ...state, specialAdmissionsBookmarkProblems: problems as ScienceProblem[], bookmarkSearching: false };
      default:
        return state;
    }
  }),
  on(BookmarkActions.findBookmarkProblemsFailure, state => ({ ...state, bookmarkSearching: false })),
  on(BookmarkActions.initializeFindBookmarkProblemsState, state => {
    const next: State = {
      ...state,
      englishBookmarkProblems: null,
      mathBookmarkProblems: null,
      nationalLanguageBookmarkProblems: null,
      physicsBookmarkProblems: null,
      chemistryBookmarkProblems: null,
      biologyBookmarkProblems: null,
      japaneseHistoryBookmarkProblems: null,
      worldHistoryBookmarkProblems: null,
      geographyBookmarkProblems: null,
      politicalEconomyBookmarkProblems: null,
      specialAdmissionsBookmarkProblems: null,
      bookmarkSearching: false
    };
    return next;
  }),

  // Find Bookmark Problem IDs -------------------------------------------------------
  on(BookmarkActions.findBookmarkProblemIds, state => ({ ...state, isBookmarkProblemIdsSearching: true })),
  on(BookmarkActions.findBookmarkProblemIdsSuccess, (state, { bookmarkProblemIds }) => ({
    ...state,
    matchedBookmarkProblemIds: bookmarkProblemIds,
    isBookmarkProblemIdsSearching: false
  })),
  on(BookmarkActions.findBookmarkProblemIdsFailure, state => ({ ...state, isBookmarkProblemIdsSearching: false })),

  on(BookmarkActions.initializeFindBookmarkProblemIdsState, state => {
    const next: State = { ...state, matchedBookmarkProblemIds: null, isBookmarkProblemIdsSearching: false };
    return next;
  })
);

export function reducer(state: State | undefined, action: Action) {
  return BookmarkReducer(state, action);
}
